import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, Footer, Question, SelectLang } from '@/components';
import { getCurrentUser, type CurrentUser } from '@/services/backend/api';
import {
  adminLoginPath,
  getAdminToken,
  isAdminAuthPath,
  shouldFetchCurrentUser,
} from '@/utils/admin-session';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  fetchUserInfo?: () => Promise<any>;
}> {
  const fetchUserInfo = async () => {
    if (!getAdminToken()) {
      return undefined;
    }

    try {
      const msg = await getCurrentUser();
      return msg.data;
    } catch {
      localStorage.removeItem('admin_token');
      if (!isAdminAuthPath(history.location.pathname)) {
        history.push(adminLoginPath);
      }
      return undefined;
    }
  };

  const { location } = history;
  if (shouldFetchCurrentUser(location.pathname, getAdminToken())) {
    const currentUser = await fetchUserInfo();
    return {
      currentUser,
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: (props) =>
      props.isMobile ? [<SelectLang key="lang" />] : [<Question key="doc" />, <SelectLang key="lang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar_url,
      title: initialState?.currentUser?.nickname || '管理员',
      render: (_, avatarChildren) => <AvatarDropdown>{avatarChildren}</AvatarDropdown>,
    },
    waterMarkProps: {
      content: initialState?.currentUser?.nickname,
    },
    footerRender: (props) => (props?.isMobile ? null : <Footer />),
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && !isAdminAuthPath(location.pathname)) {
        history.push(adminLoginPath);
        return;
      }

      if (
        initialState?.currentUser &&
        (initialState.currentUser.primary_role !== 'admin' ||
          initialState.currentUser.account_status !== 'active') &&
        !isAdminAuthPath(location.pathname)
      ) {
        localStorage.removeItem('admin_token');
        history.push(adminLoginPath);
      }
    },
    bgLayoutImgList: [],
    childrenRender: (children) => (
      <>
        <div className="admin-layout-page-shell">{children}</div>
        {isDev && (
          <SettingDrawer
            disableUrlParams
            enableDarkTheme
            settings={initialState?.settings}
            onSettingChange={(settings) => {
              setInitialState((preInitialState) => ({
                ...preInitialState,
                settings,
              }));
            }}
          />
        )}
      </>
    ),
    ...initialState?.settings,
  };
};

export const request: RequestConfig = {
  baseURL: '/api/v1',
  ...errorConfig,
};
